import { SortChart, ThreatsChartView } from '$/types'
import { IQRAGraphCsvExportParams } from 'app/services/api/qra/graphs'

import {
  ChartGroupingOptionsDto,
  ChartSortOptionsDto,
  IQRAGraphBools,
  IQRAGraphExportQueryParams,
  IThreatCategoryTooltipGroup,
  QRAGraphType
} from 'features/RiskManagement/Components/Graphs'

import { convertRiskUnitToAPIRiskValueParameter } from '../../LocalFilter'
import { RiskForecastGraphs } from './RiskForecastGraphs'

export type ChartRow = {
  category: string
  originalIndex: number
  primaryValue?: number
  tooltipGroup?: IThreatCategoryTooltipGroup
  [key: string]: number | string | IThreatCategoryTooltipGroup | undefined
}

export const comparePrimaryRiskValues = (
  left: number | undefined,
  right: number | undefined,
  direction: 'asc' | 'desc'
) => {
  if (left == null && right == null) {
    return 0
  }

  if (left == null) {
    return 1
  }

  if (right == null) {
    return -1
  }

  return direction === 'asc' ? left - right : right - left
}

export const sortChartRows = (
  rows: ChartRow[],
  sortBy: SortChart
): ChartRow[] => {
  if (sortBy === 'default') {
    return rows
  }

  return [...rows].sort((left, right) => {
    let comparison: number

    if (sortBy === 'risk-asc') {
      comparison = comparePrimaryRiskValues(
        left.primaryValue,
        right.primaryValue,
        'asc'
      )
    } else if (sortBy === 'risk-desc') {
      comparison = comparePrimaryRiskValues(
        left.primaryValue,
        right.primaryValue,
        'desc'
      )
    } else if (sortBy === 'alphabetical-asc') {
      comparison = left.category.localeCompare(right.category, undefined, {
        sensitivity: 'base'
      })
    } else {
      comparison = right.category.localeCompare(left.category, undefined, {
        sensitivity: 'base'
      })
    }

    return comparison === 0
      ? left.originalIndex - right.originalIndex
      : comparison
  })
}

export const buildGraphSelections = (
  selectedGraphType: QRAGraphType
): IQRAGraphBools => ({
  [QRAGraphType.THREATS]: selectedGraphType === QRAGraphType.THREATS,
  [QRAGraphType.CONSEQUENCES]: selectedGraphType === QRAGraphType.CONSEQUENCES,
  [QRAGraphType.RISK_BY_LINE]: selectedGraphType === QRAGraphType.RISK_BY_LINE,
  [QRAGraphType.LINEAR_REFERENCE]:
    selectedGraphType === QRAGraphType.LINEAR_REFERENCE,
  [QRAGraphType.RISK_FORECAST]:
    selectedGraphType === QRAGraphType.RISK_FORECAST,
  [QRAGraphType.RISK_BY_SIGNIFICANCE_LEVEL]:
    selectedGraphType === QRAGraphType.RISK_BY_SIGNIFICANCE_LEVEL,
  [QRAGraphType.RISK_LEVEL_BY_OUTCOME_SCENARIO]:
    selectedGraphType === QRAGraphType.RISK_LEVEL_BY_OUTCOME_SCENARIO,
  [QRAGraphType.RISK_BY_RELEASE_TYPE]:
    selectedGraphType === QRAGraphType.RISK_BY_RELEASE_TYPE,
  [QRAGraphType.RISK_BY_SYSTEM]:
    selectedGraphType === QRAGraphType.RISK_BY_SYSTEM,
  [QRAGraphType.RISK_BY_CONSEQUENCE_AREAS]:
    selectedGraphType === QRAGraphType.RISK_BY_CONSEQUENCE_AREAS
})

export type ThreatsChartCsvExportQueryParams = {
  sortBy: SortChart
  chartView: ThreatsChartView
  groupingKey?: string
  csvExportParams: IQRAGraphCsvExportParams
  assessmentId?: string
  baselineId?: number
}

export const getRiskByThreatCsvExportQueryParams = ({
  sortBy,
  chartView,
  groupingKey,
  csvExportParams,
  assessmentId,
  baselineId
}: ThreatsChartCsvExportQueryParams): IQRAGraphExportQueryParams => {
  return {
    systemScopeId: Number.parseInt(csvExportParams.systemScopeId),
    continuousRouteId: csvExportParams.continuousRouteId
      ? Number.parseInt(csvExportParams.continuousRouteId)
      : null,
    assessmentId: assessmentId ? Number.parseInt(assessmentId) : baselineId,
    savedStateId: null,
    threatConfigurationKey: groupingKey || '',
    sortOptions: sortByToChartSortOption(sortBy),
    groupingOptions:
      chartView === 'threat-category'
        ? ChartGroupingOptionsDto.ThreatCategory
        : ChartGroupingOptionsDto.Threat,
    graphRequestParams: {
      graphSelection: buildGraphSelections(QRAGraphType.THREATS),
      localFilter: csvExportParams.localFilter || {},
      riskValue:
        csvExportParams.riskUnit == null
          ? {}
          : convertRiskUnitToAPIRiskValueParameter(csvExportParams.riskUnit)
    }
  }
}

export type ChartCsvExportQueryParams = {
  sortBy: SortChart
  graphType?: QRAGraphType
  forecastChartType?: RiskForecastGraphs
  csvExportParams: IQRAGraphCsvExportParams
  assessmentId?: string
  baselineId?: number
  baselineAcceptanceCategory?: number[]
  wisAcceptanceCategory?: number[]
  baselineSystemIds?: number[]
  scenarioSystemIds?: number[]
  enabledThreatLabels?: string[]
}

const getPieCsvExportSelections = ({
  baselineAcceptanceCategory,
  wisAcceptanceCategory,
  baselineSystemIds,
  scenarioSystemIds,
  enabledThreatLabels
}: Pick<
  ChartCsvExportQueryParams,
  | 'baselineAcceptanceCategory'
  | 'wisAcceptanceCategory'
  | 'baselineSystemIds'
  | 'scenarioSystemIds'
  | 'enabledThreatLabels'
>) => ({
  ...(baselineAcceptanceCategory == null ? {} : { baselineAcceptanceCategory }),
  ...(wisAcceptanceCategory == null ? {} : { wisAcceptanceCategory }),
  ...(baselineSystemIds == null ? {} : { baselineSystemIds }),
  ...(scenarioSystemIds == null ? {} : { scenarioSystemIds }),
  ...(enabledThreatLabels == null ? {} : { enabledThreatLabels })
})

export const getChartCsvExportQueryParams = ({
  sortBy,
  graphType = QRAGraphType.CONSEQUENCES,
  csvExportParams,
  assessmentId,
  baselineId,
  baselineAcceptanceCategory,
  wisAcceptanceCategory,
  baselineSystemIds,
  scenarioSystemIds,
  forecastChartType,
  enabledThreatLabels
}: ChartCsvExportQueryParams): IQRAGraphExportQueryParams => {
  return {
    systemScopeId: Number.parseInt(csvExportParams.systemScopeId),
    continuousRouteId: csvExportParams.continuousRouteId
      ? Number.parseInt(csvExportParams.continuousRouteId)
      : null,
    assessmentId: assessmentId ? Number.parseInt(assessmentId) : baselineId,
    savedStateId: csvExportParams.savedStateId
      ? Number.parseInt(csvExportParams.savedStateId)
      : null,
    threatConfigurationKey: '',
    sortOptions: sortByToChartSortOption(sortBy),
    groupingOptions: ChartGroupingOptionsDto.None,
    ...getPieCsvExportSelections({
      baselineAcceptanceCategory,
      wisAcceptanceCategory,
      baselineSystemIds,
      scenarioSystemIds,
      enabledThreatLabels
    }),
    forecastChartType: forecastChartType,
    graphRequestParams: {
      graphSelection: buildGraphSelections(graphType),
      localFilter: csvExportParams.localFilter || {},
      riskValue:
        csvExportParams.riskUnit == null
          ? {}
          : convertRiskUnitToAPIRiskValueParameter(csvExportParams.riskUnit)
    }
  }
}

export const sortByToChartSortOption = (
  sortBy: SortChart
): ChartSortOptionsDto => {
  switch (sortBy) {
    case 'default':
      return ChartSortOptionsDto.Default
    case 'risk-asc':
      return ChartSortOptionsDto.RiskAscending
    case 'risk-desc':
      return ChartSortOptionsDto.RiskDescending
    case 'alphabetical-asc':
      return ChartSortOptionsDto.AlphabeticalAscending
    case 'alphabetical-desc':
      return ChartSortOptionsDto.AlphabeticalDescending

    default:
      return ChartSortOptionsDto.Default
  }
}
